import { useMemo } from "react";

import type { Owner } from "../rest/account";
import { useGetOwners } from "./useGetOwners";

export interface UseGetCurrentOwnerOptions {
  accountId?: string;
  enabled?: boolean;
}

/**
 * Hook to get the current owner for an account.
 *
 * For individual accounts, returns the primary owner.
 * For legal entity accounts, returns the account opener (the individual who opened the account).
 *
 * This is the current standard logic used across the system for determining which owner is making the request
 * Eventually we will need to source authorized individuals instead of the account opener
 */
export const useGetCurrentOwner = (options: UseGetCurrentOwnerOptions = {}) => {
  const { accountId, enabled = true } = options;

  const { owners, isLoading } = useGetOwners({
    accountId: accountId ?? "",
    options: { enabled: enabled && !!accountId },
  });

  const currentOwner = useMemo((): Owner | undefined => {
    if (!owners) {
      return undefined;
    }

    // Find the primary owner
    const primaryOwner = owners.find((owner) => owner.primary === true);

    // If primary owner is a legal entity, we need the account opener
    if (primaryOwner?.is_legal_entity) {
      return owners.find((owner) => owner.is_legal_account_opener);
    }

    // Otherwise, use the primary owner (individual account)
    return primaryOwner;
  }, [owners]);

  return {
    currentOwner,
    owners,
    isLoading,
  };
};

export default useGetCurrentOwner;
